import VizExIconButton from 'visitor-ui-component-library/button/VizExIconButton';
import VizExIcon from 'visitor-ui-component-library/icon/VizExIcon';
import { useState, useCallback, useRef, useEffect } from 'react';
import { SendButton } from '../message-editor/SendButton';
import CloseIcon from '../visitor-widget/CloseIcon';
import styled from 'styled-components';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { EERIE, OBSIDIAN, OLAF } from 'visitor-ui-component-library/constants/WidgetColors';
import { DEFAULT_TEXT_COLOR } from 'visitor-ui-component-library/theme/ColorConstants';
import I18n from 'I18n';
import VizExFormControl from 'visitor-ui-component-library/form-control/VizExFormControl';
import { adjustLuminance } from 'visitor-ui-component-library/utils/adjustLuminance';
import VizExTooltip from 'visitor-ui-component-library/tooltip/VizExTooltip';
import { useHover, useVisuallyHidden } from 'react-aria';
import { useButton } from '@react-aria/button';
import { getCurrentWelcomeMessageDimensionStyles, getMaxWelcomeMessageContainerWidth, MAX_WIDTH_AI_WELCOME_MESSAGE } from './util/getAIWelcomeMessageAnimationStyles';
import { getMinWidth, getMaxWidth } from './util/getMinAndMaxInitialMessageWidth';
import { formatHtml } from 'sanitize-text/sanitizers/HtmlSanitizer';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const DEFAULT_MAX_MESSAGE_LENGTH = 1024;
const AIWelcomeMessageContentWrapper = styled.div.withConfig({
  displayName: "AIWelcomeMessageContent__AIWelcomeMessageContentWrapper",
  componentId: "ygue29-0"
})(["position:relative;"]);
const WelcomeMessageText = styled.div.attrs(({
  showWelcomeMessageAsButton
}) => Object.assign({}, showWelcomeMessageAsButton && {
  'aria-haspopup': 'dialog',
  'aria-label': I18n.text('conversations-visitor-experience-components.visitorExperienceAriaLabels.open'),
  'aria-describedby': 'ai-initial-message'
})).withConfig({
  displayName: "AIWelcomeMessageContent__WelcomeMessageText",
  componentId: "ygue29-1"
})(["border:", ";display:flex;align-items:center;", ";transition:width 0.5s linear,height 0.5s linear;padding:", ";position:relative;background:", ";border-radius:40px;flex-wrap:nowrap;justify-content:space-between;cursor:", ";"], ({
  theme,
  useDefaultColor
}) => `2px solid ${useDefaultColor ? DEFAULT_TEXT_COLOR : theme.colors.primary}`, props => getCurrentWelcomeMessageDimensionStyles(props), ({
  mobile
}) => mobile ? '18px 24px' : '18px 32px', OLAF, ({
  showWelcomeMessageAsButton
}) => showWelcomeMessageAsButton ? 'default' : 'text');
const StyledIconButton = styled(VizExIconButton).withConfig({
  displayName: "AIWelcomeMessageContent__StyledIconButton",
  componentId: "ygue29-2"
})(["position:absolute;top:-32px;right:24px;height:24px;width:24px;border-radius:20px;background-color:", ";border:1px solid ", ";font-size:12px;&:hover:enabled{background-color:", ";border-color:", ";}&:active:enabled{background-color:", ";border-color:", ";}"], EERIE, EERIE, adjustLuminance(EERIE, 10), adjustLuminance(EERIE, 10), adjustLuminance(EERIE, -10), adjustLuminance(EERIE, -10));
const InputWrapper = styled.div.withConfig({
  displayName: "AIWelcomeMessageContent__InputWrapper",
  componentId: "ygue29-3"
})(["width:100%;align-self:stretch;"]);
const IntialMessageTextWrapper = styled.div.withConfig({
  displayName: "AIWelcomeMessageContent__IntialMessageTextWrapper",
  componentId: "ygue29-4"
})(["flex-direction:column;display:flex;align-items:flex-start;animation:fadeIn 0.75s ease-in forwards;@keyframes fadeIn{0%{opacity:0;}100%{opacity:1;}}"]);
const StyledParagraph = styled.p.withConfig({
  displayName: "AIWelcomeMessageContent__StyledParagraph",
  componentId: "ygue29-5"
})(["outline:none !important;font-size:", ";height:100%;word-break:break-word;border:", ";&:empty{::before{content:'", "';opacity:0.6;}}"], ({
  isIOSMobile
}) => isIOSMobile && '16px', ({
  error
}) => error ? '1px solid red' : 'none', ({
  placeholder
}) => placeholder);
const ToolTipAliasWrapper = styled.div.withConfig({
  displayName: "AIWelcomeMessageContent__ToolTipAliasWrapper",
  componentId: "ygue29-6"
})(["position:absolute;top:-5px;display:flex;left:calc((50% - 56px / 2) / 2);"]);
const AIWelcomeMessageContent = ({
  onClose,
  initialMessage,
  publishMessage,
  mobile,
  onClick,
  isAIChatBot,
  isIOSMobile,
  showWelcomeMessageAsButton,
  children,
  avatarsCount,
  widgetLocation,
  isInPreviewMode,
  messageBubbleContainerRef,
  useDefaultColor
}) => {
  const welcomeMsgTextBoxRef = useRef(null);
  const {
    buttonProps
  } = useButton({
    onPress: onClick,
    elementType: 'div'
  }, welcomeMsgTextBoxRef);
  const {
    visuallyHiddenProps
  } = useVisuallyHidden();
  const inputRef = useRef(null);
  const [showInputField, setShowInputField] = useState(false);
  const [inputMessage, setInputMessage] = useState('');
  const [showTooltip, setShowTooltip] = useState(false);
  const [inputHasBlurred, setInputHasBlurred] = useState(false);
  const [{
    width: defaultWidth,
    height: defaultHeight
  }, setDefaultWelcomeMessageDimensions] = useState({
    width: -1,
    height: -1
  });
  useEffect(() => {
    if (welcomeMsgTextBoxRef !== null && welcomeMsgTextBoxRef !== void 0 && welcomeMsgTextBoxRef.current) {
      let {
        width,
        height
      } = welcomeMsgTextBoxRef.current.getBoundingClientRect();
      //in preview mode on mobile the css: scale(0.75) is applied, the following offset adjusts that
      if (isInPreviewMode && mobile) {
        width *= 1 / 0.75;
        height *= 1 / 0.75;
      }
      setDefaultWelcomeMessageDimensions(dimenstions => Object.assign({}, dimenstions, {
        width,
        height
      }));
    }
  }, [welcomeMsgTextBoxRef, isInPreviewMode, mobile]);
  const hasTextContent = Boolean(inputMessage.trim());
  const handleInputBlur = useCallback(() => {
    setInputHasBlurred(true);
    if (!hasTextContent) {
      setShowInputField(false);
    }
  }, [hasTextContent]);
  const {
    hoverProps
  } = useHover({
    onHoverStart: () => {
      if (!hasTextContent && document.activeElement !== inputRef.current) {
        setShowTooltip(true);
      }
    },
    onHoverEnd: () => {
      setShowTooltip(false);
    }
  });
  const inputCallbackRef = useCallback(inputElement => {
    if (inputElement) {
      inputElement.focus();
      inputRef.current = inputElement;
    }
  }, []);
  const messageExceededMaxLength = inputMessage.length > DEFAULT_MAX_MESSAGE_LENGTH;
  const handleChange = e => {
    const {
      innerText
    } = e.target;
    setInputMessage(innerText);
  };
  const handlePublish = () => {
    if (!inputMessage || messageExceededMaxLength) return;
    publishMessage(inputMessage);
  };
  const handleKeyDown = e => {
    if (e.key === 'Enter' && e.shiftKey === false) {
      handlePublish();
      e.preventDefault();
      e.stopPropagation();
      return false;
    }
    return true;
  };
  const handleInputFocus = useCallback(() => {
    setShowTooltip(false);
  }, [setShowTooltip]);
  const handlePaste = event => {
    var _event$clipboardData;
    event.preventDefault();
    const text = event === null || event === void 0 || (_event$clipboardData = event.clipboardData) === null || _event$clipboardData === void 0 ? void 0 : _event$clipboardData.getData('text/plain');
    const selection = window.getSelection();
    if (!(selection !== null && selection !== void 0 && selection.rangeCount)) {
      return;
    }
    selection.deleteFromDocument();
    const node = document.createTextNode(text);
    selection.getRangeAt(0).insertNode(node);
    const textNodes = [];
    const parent = selection.getRangeAt(0).commonAncestorContainer;
    const childNodes = parent.childNodes;
    for (const child of childNodes) {
      textNodes.push(child.nodeValue);
    }
    const inputText = textNodes.join('');
    setInputMessage(inputText || '');
    selection.collapseToEnd();
  };
  const maxWelcomeMessageWidth = getMaxWelcomeMessageContainerWidth(isAIChatBot, mobile);
  return /*#__PURE__*/_jsxs(AIWelcomeMessageContentWrapper, {
    children: [!mobile && isAIChatBot && /*#__PURE__*/_jsx(ToolTipAliasWrapper, {
      children: /*#__PURE__*/_jsx(VizExTooltip, {
        content: I18n.text('conversations-visitor-experience-components.welcomeMessageTextArea.tooltip'),
        open: showTooltip && defaultWidth >= MAX_WIDTH_AI_WELCOME_MESSAGE,
        placement: "top center",
        children: /*#__PURE__*/_jsx("div", Object.assign({}, visuallyHiddenProps))
      })
    }), /*#__PURE__*/_jsxs(WelcomeMessageText, Object.assign({
      "data-test-id": "ai-welcome-message-text-box",
      onClick: () => {
        if (showWelcomeMessageAsButton) {
          onClick();
          return;
        }
        if (messageBubbleContainerRef !== null && messageBubbleContainerRef !== void 0 && messageBubbleContainerRef.current && defaultWidth < maxWelcomeMessageWidth) {
          messageBubbleContainerRef.current.style.width = `${maxWelcomeMessageWidth}px`;
        }
        setShowInputField(true);
        if (showInputField && inputRef !== null && inputRef !== void 0 && inputRef.current) {
          inputRef.current.focus();
        }
      },
      mobile: mobile,
      isAIChatBot: isAIChatBot,
      showWelcomeMessageAsButton: showWelcomeMessageAsButton,
      showInputField: showInputField,
      ref: welcomeMsgTextBoxRef,
      defaultWidth: defaultWidth,
      defaultHeight: defaultHeight,
      tabIndex: 0,
      hasTextContent: hasTextContent
    }, mobile || showWelcomeMessageAsButton ? {} : hoverProps, showWelcomeMessageAsButton && buttonProps, {
      useDefaultColor: useDefaultColor,
      onTransitionEnd: () => {
        if (inputHasBlurred) {
          if (messageBubbleContainerRef !== null && messageBubbleContainerRef !== void 0 && messageBubbleContainerRef.current) {
            messageBubbleContainerRef.current.style.width = 'auto';
          }
          setInputHasBlurred(false);
        }
        if (showInputField && inputRef !== null && inputRef !== void 0 && inputRef.current) {
          const inputIsFocused = document.activeElement === inputRef.current;
          if (!inputIsFocused) {
            inputRef.current.focus();
          }
        }
      },
      children: [showInputField && /*#__PURE__*/_jsx(InputWrapper, {
        children: /*#__PURE__*/_jsx(VizExFormControl, {
          validationMessage: messageExceededMaxLength ? I18n.text('conversations-visitor-experience-components.welcomeMessageTextArea.validationMessage') : '',
          error: messageExceededMaxLength,
          style: {
            height: messageExceededMaxLength ? 'auto' : '100%'
          },
          children: () => /*#__PURE__*/_jsx(StyledParagraph, {
            ref: inputCallbackRef,
            contentEditable: true,
            onInput: handleChange,
            onKeyDown: handleKeyDown,
            onPaste: handlePaste,
            error: messageExceededMaxLength,
            onFocus: handleInputFocus,
            onBlur: handleInputBlur,
            placeholder: I18n.text('conversations-visitor-experience-components.welcomeMessageTextArea.placeholder'),
            tabIndex: 0,
            "data-test-id": "welcome-message-input",
            isIOSMobile: isIOSMobile
          })
        })
      }), !showInputField && /*#__PURE__*/_jsxs(IntialMessageTextWrapper, {
        children: [/*#__PURE__*/_jsx("p", {
          id: "ai-initial-message",
          "data-test-id": "initial-message-text",
          style: {
            color: OBSIDIAN,
            fontSize: 14,
            lineHeight: '24px',
            margin: 0,
            width: 'max-content',
            minWidth: getMinWidth(isAIChatBot, avatarsCount),
            maxWidth: getMaxWidth(isAIChatBot, mobile),
            wordBreak: 'break-word'
          }
          /* eslint-disable-next-line react/no-danger */,
          dangerouslySetInnerHTML: {
            __html: formatHtml(initialMessage)
          }
        }), isAIChatBot && /*#__PURE__*/_jsx("div", {
          style: {
            color: EERIE,
            fontSize: 12,
            lineHeight: '18px'
          },
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: 'conversations-visitor-experience-components.default.aiAgent'
          })
        })]
      }), showInputField && /*#__PURE__*/_jsx(SendButton, {
        disabled: messageExceededMaxLength || !hasTextContent,
        style: {
          flexShrink: 0
        },
        onClick: handlePublish,
        className: "m-left-1",
        "data-test-id": "ai-welcome-msg-send-button"
      })]
    })), /*#__PURE__*/_jsx(StyledIconButton, {
      shape: "circle",
      onClick: onClose,
      "data-test-id": "ai-welcome-msg-close-button",
      widgetLocation: widgetLocation,
      "aria-label": I18n.text('conversations-visitor-experience-components.welcomeMessageTextArea.close'),
      children: /*#__PURE__*/_jsx(VizExIcon, {
        icon: /*#__PURE__*/_jsx(CloseIcon, {
          "aria-label": I18n.text('conversations-visitor-experience-components.visitorExperienceAriaLabels.dismiss'),
          height: 8,
          width: 8,
          color: OLAF
        })
      })
    }), children]
  });
};
AIWelcomeMessageContent.displayName = 'AIWelcomeMessageContent';
export default AIWelcomeMessageContent;